/* istanbul ignore file */
import { useTranslation } from 'react-i18next'

import {
  Bar,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { useTheme } from 'styled-components'

import { FormatNumber, Text } from '@reveni-io/reveni-ui'

import { timeRangeValues } from '@/common/components/TimeRangeSelect/constants'
import CustomLabel from '@/metrics/components/CustomLabel/CustomLabel'
import TooltipChart from '@/metrics/components/TooltipChart/TooltipChart'
import { getDateByTimeRange } from '@/metrics/helpers/helpers'

import * as Styles from './styles'

const RatioBarsChart = ({ data, series, timeRangeSelected, fullWidth }) => {
  const theme = useTheme()
  const { t, i18n } = useTranslation()

  const getChartWidth = () => {
    if (timeRangeSelected === timeRangeValues.lastMonth && !fullWidth) {
      return 1300
    }

    return '100%'
  }

  return (
    <ResponsiveContainer width={getChartWidth()} height="100%">
      <ComposedChart data={data} barGap={0} barSize={8} margin={{ top: 10, right: -30, left: -40, bottom: 0 }}>
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="date"
          tickFormatter={date => getDateByTimeRange(date, i18n.language, timeRangeSelected)}
          axisLine={false}
          tickLine={false}
          style={{
            fontSize: theme.texts.allCapsS.fontSize,
            fontFamily: theme.texts.allCapsS.fontFamily,
            textTransform: 'uppercase',
          }}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          yAxisId="left"
          style={{
            fontSize: theme.texts.allCapsS.fontSize,
            fontFamily: theme.texts.allCapsS.fontFamily,
            textTransform: 'uppercase',
          }}
        />
        <YAxis
          yAxisId="right"
          orientation="right"
          axisLine={false}
          tickLine={false}
          domain={[0, 100]}
          tickFormatter={value => `${value}%`}
          style={{
            fontSize: theme.texts.allCapsS.fontSize,
            fontFamily: theme.texts.allCapsS.fontFamily,
            textTransform: 'uppercase',
          }}
        />
        <Tooltip
          cursor={{ fill: 'transparent' }}
          content={
            <TooltipChart>
              {payload => (
                <Styles.TooltipContainer>
                  <Text type="allCapsS" customStyles={{ textTransform: 'uppercase' }}>
                    {getDateByTimeRange(payload[0]?.payload.date, i18n.language, timeRangeValues.lastWeek)}
                  </Text>
                  <Styles.TooltipCountsContainer>
                    {series.bars.map(bar => (
                      <Styles.Legend key={bar.field}>
                        <Styles.LegendSquare color={bar.color} />
                        <Text type="bodySRegular">
                          {t(bar.label)}: <FormatNumber style="decimal">{payload[0]?.payload[bar.field]}</FormatNumber>
                        </Text>
                      </Styles.Legend>
                    ))}
                  </Styles.TooltipCountsContainer>
                  <Text type="bodySRegular">
                    <strong>
                      {t(series.line.label)}:{' '}
                      <FormatNumber style="percent">{payload[0]?.payload[series.line.field]}</FormatNumber>
                    </strong>
                  </Text>
                </Styles.TooltipContainer>
              )}
            </TooltipChart>
          }
        />
        {series.bars.map(bar => (
          <Bar key={bar.field} yAxisId="left" dataKey={bar.field} fill={bar.color} radius={[2, 2, 0, 0]} />
        ))}
        <Line
          type="linear"
          dataKey={series.line.field}
          stroke={series.line.color}
          strokeWidth={2}
          dot={false}
          activeDot={false}
          yAxisId="right"
        >
          <LabelList
            dataKey={series.line.field}
            position="center"
            content={<CustomLabel color={series.line.labelColor} />}
          />
        </Line>
      </ComposedChart>
    </ResponsiveContainer>
  )
}

export default RatioBarsChart
